/**
 * /fine-tuning-roi — PUBLIC lead magnet.
 * ========================================
 * Stateless calculator: "Should we fine-tune?"
 * Inputs: model, monthly call volume, prompt size, few-shot savings, training size.
 * Output: monthly cost without/with fine-tuning, payback months, verdict.
 *
 * Brand: Slate-950, emerald accents, soft amber for warnings.
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import SEOHead from '../components/SEOHead';
import {
  Calculator, Sparkles, ArrowLeft, Loader2, TrendingDown, ArrowRight, BookOpen,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (n, d = 0) => `$${(n || 0).toLocaleString(undefined, { maximumFractionDigits: d })}`;

const VERDICT_TONE = {
  ship_it: { bg: 'bg-emerald-500/15', border: 'border-emerald-400/40', text: 'text-emerald-200' },
  consider: { bg: 'bg-emerald-500/15', border: 'border-emerald-400/40', text: 'text-cyan-200' },
  marginal: { bg: 'bg-amber-500/15', border: 'border-amber-400/40', text: 'text-amber-200' },
  fine_tuning_not_worth_it: { bg: 'bg-rose-500/15', border: 'border-rose-400/40', text: 'text-rose-200' },
};

const FineTuningROI = () => {
  const [models, setModels] = useState([]);
  const [form, setForm] = useState({
    model: 'gpt-4o-mini',
    monthly_calls: 1_000_000,
    avg_prompt_tokens: 2_200,
    avg_output_tokens: 180,
    few_shot_token_savings: 1_400,
    training_examples: 2_000,
    avg_train_tokens_per_example: 800,
    amortization_months: 6.0,
  });
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/fine-tuning-roi/models`).then(r => setModels(r.data.models)).catch(() => {});
    // auto-run sample on first load for instant value
    axios.get(`${API_URL}/fine-tuning-roi/sample`).then(r => setResult(r.data.sample_output)).catch(() => {});
  }, []);

  const updateField = (k) => (e) => {
    const v = e?.target ? e.target.value : e;
    setForm(prev => ({ ...prev, [k]: typeof v === 'string' && !isNaN(parseFloat(v)) ? parseFloat(v) : v }));
  };

  const run = async () => {
    setRunning(true);
    try {
      const { data } = await axios.post(`${API_URL}/fine-tuning-roi/calculate`, form);
      if (!data.ok) {
        toast.error(data.error || 'Calculation failed');
      } else {
        setResult(data);
      }
    } catch (e) {
      toast.error('Could not calculate. Please try again.');
    } finally {
      setRunning(false);
    }
  };

  const verdictTone = result ? VERDICT_TONE[result.verdict] || VERDICT_TONE.marginal : null;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="fine-tuning-roi-page">
      <SEOHead
        title="Fine-tuning ROI Calculator — Free | CARTIEAI"
        description="Should you fine-tune? Free calculator: monthly savings, payback period, training cost amortization. No signup."
      />
      {/* Sticky nav */}
      <nav className="sticky top-0 z-30 backdrop-blur bg-slate-950/85 border-b border-white/5">
        <div className="max-w-6xl mx-auto px-5 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-slate-200 hover:text-white text-sm">
            <ArrowLeft className="w-4 h-4" /> CARTIEAI
          </Link>
          <Link to="/" data-testid="ftr-cta-home">
            <Button size="sm" className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold">
              Start free <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
            </Button>
          </Link>
        </div>
      </nav>

      <main className="max-w-6xl mx-auto px-5 py-10">
        <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-emerald-500/10 border border-emerald-400/30 mb-4">
          <BookOpen className="w-3.5 h-3.5 text-emerald-300" />
          <span className="text-[11px] font-semibold tracking-widest text-emerald-200 uppercase">FREE · NO SIGNUP · 4 MODELS</span>
        </div>
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight">
          <span className="text-emerald-400">
            Should you fine-tune?
          </span>
        </h1>
        <p className="text-slate-400 text-base mt-3 max-w-2xl">
          Real crossover math: monthly cost without fine-tuning vs. with, plus payback months.
          Jan-2026 OpenAI / Anthropic / Together AI pricing.
        </p>

        <div className="grid lg:grid-cols-5 gap-6 mt-8">
          {/* INPUTS */}
          <Card className="lg:col-span-2 bg-slate-900/60 border-white/5">
            <CardContent className="p-5 space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-400">Model</Label>
                <Select value={form.model} onValueChange={updateField('model')}>
                  <SelectTrigger data-testid="ftr-model-select" className="bg-slate-950 border-white/10 mt-1.5">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {models.map(m => <SelectItem key={m.id} value={m.id}>{m.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-wide text-slate-400">Monthly calls</Label>
                  <Input type="number" value={form.monthly_calls} onChange={updateField('monthly_calls')} data-testid="ftr-monthly-calls" className="bg-slate-950 border-white/10 mt-1.5" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wide text-slate-400">Avg prompt tokens</Label>
                  <Input type="number" value={form.avg_prompt_tokens} onChange={updateField('avg_prompt_tokens')} data-testid="ftr-prompt-tokens" className="bg-slate-950 border-white/10 mt-1.5" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wide text-slate-400">Avg output tokens</Label>
                  <Input type="number" value={form.avg_output_tokens} onChange={updateField('avg_output_tokens')} data-testid="ftr-output-tokens" className="bg-slate-950 border-white/10 mt-1.5" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wide text-slate-400">Few-shot savings</Label>
                  <Input type="number" value={form.few_shot_token_savings} onChange={updateField('few_shot_token_savings')} data-testid="ftr-fewshot-savings" className="bg-slate-950 border-white/10 mt-1.5" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wide text-slate-400">Training examples</Label>
                  <Input type="number" value={form.training_examples} onChange={updateField('training_examples')} data-testid="ftr-training-examples" className="bg-slate-950 border-white/10 mt-1.5" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wide text-slate-400">Train tokens / example</Label>
                  <Input type="number" value={form.avg_train_tokens_per_example} onChange={updateField('avg_train_tokens_per_example')} data-testid="ftr-train-tokens" className="bg-slate-950 border-white/10 mt-1.5" />
                </div>
              </div>

              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-400">Amortization (months)</Label>
                <Input type="number" value={form.amortization_months} onChange={updateField('amortization_months')} data-testid="ftr-amortization" className="bg-slate-950 border-white/10 mt-1.5" />
              </div>

              <Button
                onClick={run}
                disabled={running}
                className="w-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold"
                data-testid="ftr-calculate-btn"
              >
                {running ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Calculating</>
                  : <><Calculator className="w-4 h-4 mr-2" /> Recalculate</>}
              </Button>
            </CardContent>
          </Card>

          {/* RESULTS */}
          <div className="lg:col-span-3 space-y-5">
            {result ? (
              <>
                <Card data-testid="ftr-verdict-card" className={`${verdictTone.bg} ${verdictTone.border} border`}>
                  <CardContent className="p-5">
                    <div className="flex items-start gap-3">
                      <Sparkles className={`w-6 h-6 ${verdictTone.text} flex-shrink-0`} />
                      <div className="flex-1">
                        <div className={`text-xs uppercase tracking-widest font-semibold ${verdictTone.text}`}>Verdict</div>
                        <div className="text-xl font-semibold text-white mt-1" data-testid="ftr-verdict-label">{result.verdict_label}</div>
                        {result.payback_months !== null && (
                          <div className="text-sm text-slate-300 mt-2">
                            Payback: <span className={`font-semibold ${verdictTone.text}`} data-testid="ftr-payback-months">{result.payback_months} months</span>
                            <span className="text-slate-400 mx-2">·</span>
                            Annual net savings: <span className="font-semibold text-emerald-200" data-testid="ftr-annual-savings">{fmt(result.annual_net_savings_usd)}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <div className="grid sm:grid-cols-2 gap-4">
                  <Card className="bg-slate-900/60 border-white/5">
                    <CardContent className="p-5">
                      <div className="text-xs uppercase tracking-wider text-slate-400">Without fine-tuning</div>
                      <div className="text-3xl font-bold text-white mt-2" data-testid="ftr-base-monthly">{fmt(result.base_monthly_cost_usd)}</div>
                      <div className="text-sm text-slate-400 mt-1">per month</div>
                    </CardContent>
                  </Card>
                  <Card className="bg-slate-900/60 border-emerald-500/20">
                    <CardContent className="p-5">
                      <div className="text-xs uppercase tracking-wider text-emerald-300">With fine-tuning</div>
                      <div className="text-3xl font-bold text-white mt-2" data-testid="ftr-ft-monthly">{fmt(result.fine_tuned_monthly_cost_usd)}</div>
                      <div className="text-sm text-slate-400 mt-1">per month</div>
                    </CardContent>
                  </Card>
                </div>

                <Card className="bg-slate-900/60 border-white/5">
                  <CardContent className="p-5">
                    <div className="text-xs uppercase tracking-wider text-slate-400 mb-3">Math breakdown</div>
                    <div className="space-y-1.5 text-sm">
                      <div className="flex justify-between"><span className="text-slate-400">One-time training cost</span><span className="text-white font-mono" data-testid="ftr-train-cost">{fmt(result.training_cost_usd_one_time)}</span></div>
                      <div className="flex justify-between"><span className="text-slate-400">Amortized training / month</span><span className="text-white font-mono">{fmt(result.amortized_training_per_month_usd)}</span></div>
                      <div className="flex justify-between"><span className="text-slate-400">Monthly savings (gross)</span><span className="text-emerald-300 font-mono">{fmt(result.monthly_savings_usd)}</span></div>
                      <div className="flex justify-between border-t border-white/10 pt-1.5 mt-1.5"><span className="text-slate-300">Net monthly (post-amortization)</span><span className="text-emerald-200 font-mono font-semibold">{fmt(result.net_monthly_savings_after_amortization_usd)}</span></div>
                    </div>
                    <p className="text-xs text-slate-400 mt-4 italic">{result.honest_caveat}</p>
                  </CardContent>
                </Card>
              </>
            ) : (
              <Card className="bg-slate-900/60 border-white/5">
                <CardContent className="p-10 text-center text-slate-400">
                  <Calculator className="w-8 h-8 mx-auto mb-3 opacity-50" />
                  Enter your numbers on the left, then click <span className="text-emerald-300">Recalculate</span>.
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        {/* CTA */}
        <Card className="mt-10 bg-gradient-to-br from-emerald-500/10 to-cyan-500/5 border-emerald-500/30">
          <CardContent className="p-5 sm:p-6 flex flex-col sm:flex-row items-start justify-between gap-3">
            <div>
              <p className="text-white font-semibold">Want this running on your actual prompts in production?</p>
              <p className="text-slate-400 text-sm mt-1 max-w-2xl">
                CARTIEAI tracks every LLM call you make → tells you which features actually pay for themselves,
                which to fine-tune, and which to compress. Free tier. Outcome pricing.
              </p>
            </div>
            <Link to="/" data-testid="ftr-cta-start">
              <Button className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold whitespace-nowrap">
                Start free <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
              </Button>
            </Link>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default FineTuningROI;
